import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import LoginModal from '@/components/molecules/login-modal';
import useModal from '@/hooks/useModal';
import { clearReturnUrl } from '@/recoil/atoms/common/auth';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { getPageName, logger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, historyBack, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';

const useAuthModal = (canOverlayClick = false, isWritePage = false) => {
  const router = useRouter();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>();
  const [customTitle, setCustomTitle] = useState<string | undefined>();
  const [customMessage, setCustomMessage] = useState<string | undefined>();
  const { showModal, closeModal } = useModal();
  const [trackInfo, setTrackInfo] = useState({
    campaign: '',
    content: '',
    buttonText: '',
  });

  const isRouteBackPath = router.pathname.includes('/content/qna/write');

  const onClickCancel = useCallback(() => {
    clearReturnUrl();
    setShowAuthModal(false);
    setRedirectUrl(undefined);
    setCustomTitle(undefined);
    setCustomMessage(undefined);
    closeModal();
    if (isWebView() && isRouteBackPath) {
      historyBack();
    }
  }, [closeModal, isRouteBackPath]);

  const onClickLogin = () => {
    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_LOGIN);
      clearReturnUrl();
      setShowAuthModal(false);
      setRedirectUrl(undefined);
      setCustomTitle(undefined);
      setCustomMessage(undefined);
      closeModal();
    }
  };

  const handleShowAuthModal = (show: boolean, url?: string, customTitle?: string, customMessage?: string) => {
    setShowAuthModal(show);
    if (url) {
      setRedirectUrl(url);
    }
    if (customTitle) {
      setCustomTitle(customTitle);
    }
    if (customMessage) {
      setCustomMessage(customMessage);
    }
  };

  useEffect(() => {
    if (showAuthModal) {
      logger({
        eventName: 'sns_login',
        pageName: getPageName(router),
      });

      if (!isWebView()) {
        showModal({
          type: MODAL_TYPE.BASIC,
          props: {
            body: (
              <LoginModal
                isWritePage={isWritePage}
                onClick={onClickCancel}
                redirectUrl={redirectUrl}
                customTitle={customTitle}
                customMessage={customMessage}
              />
            ),
            onClickOverlay: canOverlayClick ? onClickCancel : undefined,
          },
        });
      } else {
        showModal({
          type: MODAL_TYPE.ALERT,
          props: {
            title: customTitle || '로그인이 필요해요',
            message: customMessage || '서비스 이용을 위해 로그인해 주세요.',
            buttons: [
              {
                label: '닫기',
                onClick: () => onClickCancel(),
              },
              {
                type: MODAL_BUTTON_TYPE.PRIMARY,
                label: '로그인',
                onClick: () => onClickLogin(),
              },
            ],
          },
        });
      }
    }
  }, [showAuthModal, redirectUrl, customTitle, customMessage]);

  useEffect(() => {
    const close = () => {
      closeModal();
    };

    window.addEventListener('popstate', close);

    return () => {
      window.removeEventListener('popstate', close);
    };
  });

  return {
    setShowAuthModal: handleShowAuthModal,
    setTrackInfo,
    trackInfo,
  };
};

export default useAuthModal;
